import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { API } from '../App';
import {
  LayoutDashboard,
  Users,
  Package,
  ChefHat,
  ShoppingCart,
  UserCircle,
  CreditCard,
  LogOut,
  Menu,
  X,
} from 'lucide-react';

const Layout = ({ children, user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_error) {
      // Clear local state even when the token has already expired.
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      navigate('/login');
    }
  };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/customers', icon: Users, label: 'Clientes' },
    { path: '/ingredients', icon: Package, label: 'Ingredientes' },
    { path: '/recipes', icon: ChefHat, label: 'Receitas' },
    { path: '/orders', icon: ShoppingCart, label: 'Pedidos' },
  ];

  if (user?.role === 'Admin') {
    menuItems.push({ path: '/users', icon: UserCircle, label: 'Usuários' });
    if (!user?.is_demo) {
      menuItems.push({ path: '/billing', icon: CreditCard, label: 'Plano e cobrança' });
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white/80 backdrop-blur-md border-r border-pink-100 shadow-sm">
        <div className="p-6 border-b border-pink-100">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-rose-400 bg-clip-text text-transparent" data-testid="app-title">
            SweetManager
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-testid="user-name">{user?.nome}</p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive ? 'default' : 'ghost'}
                  className={`w-full justify-start ${
                    isActive
                      ? 'bg-gradient-to-r from-pink-500 to-rose-400 text-white shadow-md'
                      : 'hover:bg-pink-50'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-pink-100">
          <Button
            variant="ghost"
            className="w-full justify-start text-red-500 hover:bg-red-50 hover:text-red-600"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white/95 backdrop-blur-md shadow-xl z-50">
            <div className="p-6 border-b border-pink-100 flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-rose-400 bg-clip-text text-transparent">
                  SweetManager
                </h1>
                <p className="text-sm text-gray-500 mt-1">{user?.nome}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="p-4 space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}>
                    <Button
                      variant={isActive ? 'default' : 'ghost'}
                      className={`w-full justify-start ${
                        isActive
                          ? 'bg-gradient-to-r from-pink-500 to-rose-400 text-white shadow-md'
                          : 'hover:bg-pink-50'
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-pink-100">
              <Button
                variant="ghost"
                className="w-full justify-start text-red-500 hover:bg-red-50 hover:text-red-600"
                onClick={handleLogout}
              >
                <LogOut className="mr-3 h-5 w-5" />
                Sair
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden bg-white/80 backdrop-blur-md border-b border-pink-100 p-4 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-bold bg-gradient-to-r from-pink-500 to-rose-400 bg-clip-text text-transparent">
            SweetManager
          </h1>
          <div className="w-10" />
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {user?.is_demo && (
            <div
              role="status"
              className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              data-testid="demo-session-banner"
            >
              <strong>Ambiente demonstrativo.</strong> Os dados são fictícios e serão apagados ao sair ou quando a sessão expirar.
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
